import React from "react";
import AdvScheduleGraph3, { recipeOwners } from "./AdvScheduleGraph3";
import ValveEditor from "./valveEditor";
import { buildCurrentRecipeSegments, channelLabel, zoneNameOf } from "./advScheduleUtils";
import { RECIPE_COLOURS } from "./DoserRecipePane";
import LoadingSpinner from "../loadingSpinner";
import { dbVals, sendArray } from "../../../components/utils/http";
import { getParamId2, wsTrans } from "../../utils/utils";
import { az, cl, globs, getTime, constant } from "../../../components/utils/utils";

const IRR_TAGS = ["SCHED", "LIGHT", "CYCLE", "TRIGGER", "SOIL", "VPD"];

function sdm(dayMins) {
  const hr = Math.floor(dayMins / 60);
  const mn = dayMins % 60;
  return `${az(hr, 2)}:${az(mn, 2)}`;
}

function showRecipeListSched(list, sched) {
  cl(list.map((l) => l.name));
  cl(sched.map((s) => ({ start: sdm(s.start), rec: list[s.recipeIdx].name })));
}

const savingOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const savingBox = {
  background: "#fff",
  borderRadius: 10,
  padding: "20px 28px",
  minWidth: 280,
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  fontFamily: "inherit",
};

const savingTitle = {
  fontSize: 16,
  fontWeight: 700,
  color: "#2c3e50",
  marginBottom: 12,
};

const savingTrack = {
  height: 10,
  borderRadius: 5,
  background: "#e8eef5",
  overflow: "hidden",
};

const savingLabel = {
  marginTop: 8,
  fontSize: 12,
  color: "#8a9aac",
  textAlign: "center",
};

let startId0;
let durId0;
let irrStartId;
let irrEndId;
let irrModeId;

function paramIds() {
  if (startId0 != null) return;
  startId0 = getParamId2(1900, "config_channels_configuration", "start_time_1");
  durId0 = getParamId2(1900, "config_channels_configuration", "duration_1_irr");
  irrStartId = getParamId2(1900, "config_channels_configuration", "start");
  irrEndId = getParamId2(1900, "config_channels_configuration", "end");
  irrModeId = getParamId2(1900, "config_channels_configuration", "irrigation_mode");
}

/** Minutes from midnight at the site. tzo is minutes to add to site-local
 *  time to get UTC (420 for Pacific Daylight), so local = UTC − tzo. */
function cloneValves(valves) {
  return (valves || []).map((v) => ({
    ...v,
    events: (v.events || []).map((ev) => ({ ...ev })),
  }));
}

function valveSigs(valves) {
  const out = {};
  (valves || []).forEach((v) => {
    out[v.id] = (v.events || [])
      .map((ev) => `${Math.round(Number(ev.dayMins) || 0)}|${Math.round(Number(ev.durMins) || 0)}|${ev.recipe || ""}`)
      .sort()
      .join(",");
  });
  return out;
}

function editedCount(savedSig, valves) {
  const now = valveSigs(valves);
  const ids = {};
  Object.keys(savedSig || {}).forEach((id) => { ids[id] = true; });
  Object.keys(now).forEach((id) => { ids[id] = true; });
  let n = 0;
  Object.keys(ids).forEach((id) => {
    if ((savedSig[id] || "") !== (now[id] || "")) n += 1;
  });
  return n;
}

export function liveClock(date, tzo) {
  const d = date || new Date();
  const utc =
    d.getUTCHours() * 60 +
    d.getUTCMinutes() +
    d.getUTCSeconds() / 60;
  const local = utc - (Number(tzo) || 0);
  return ((local % 1440) + 1440) % 1440;
}

export default class DoserSchedule3 extends React.Component {
  constructor(props) {
    super(props);
//     cl(this.props)
    this.state = {
      loaded: false,
      recipes: [],
      segments: [],
      valves: [],
      stub: "",
      nowMins: liveClock(new Date(), props.parms && props.parms.tzo),
      editingId: null,
      zonesToSave: 0,
      zonesSaved: 0,
    };
    this.savedSig = {};
    this.savedValves = [];
    this.subscribe_newZoneData = globs.events.subscribe("newZoneData", this.newZoneData);
    this.subscribe_savePageEvent = globs.events.subscribe("savePageEvent", this.savePage);
  }

  componentDidMount() {
    this.loadBoard();
    let {mode}=this.props.parms
    cl(mode)
    this.setState({mode})
    this.clockTimer = setInterval(() => {
      const tzo = this.props.parms && this.props.parms.tzo;
      this.setState({ nowMins: liveClock(new Date(), tzo) });
    }, 1000);
  }

  componentDidUpdate(prev) {
    const a = prev.parms || {};
    const b = this.props.parms || {};
    if (
      a.valves !== b.valves ||
      a.channels !== b.channels ||
      a.recipeSegments !== b.recipeSegments ||
      a.recipes !== b.recipes ||
      a.zones !== b.zones
    ) {
      this.loadBoard();
    }
  }

  componentWillUnmount() {
    if (this.subscribe_newZoneData && this.subscribe_newZoneData.remove) {
      this.subscribe_newZoneData.remove();
    }
    if (this.subscribe_savePageEvent && this.subscribe_savePageEvent.remove) {
      this.subscribe_savePageEvent.remove();
    }
    this.armSave(false);
    clearInterval(this.clockTimer);
  }

  newZoneData = (msg) => {
    if (msg && msg.cmd === "dataAll") this.loadBoard();
  };

  loadBoard = () => {
    paramIds();
    const pa = this.props.parms || {};
    const channels = pa.channels || {};
    const zones = pa.zones || [];
    const recipes = (pa.recipes || [])
      .filter((r) => r && r.recipeId && r.recipeId !== "none")
      .map((r, i) => ({
        ...r,
        color: r.color || RECIPE_COLOURS[i % RECIPE_COLOURS.length],
      }));
    const held = recipeOwners(pa.recipeSegments || []);
    const carried = (dayMins, durMins) => {
      const start = Math.max(0, Math.floor(Number(dayMins) || 0));
      const len = Math.max(0, Math.round(Number(durMins) || 0));
      if (held[start]) return held[start];
      for (let k = 0; k < len; k++) {
        const id = held[(start + k) % 1440];
        if (id) return id;
      }
      return "";
    };
    const keys = Array.isArray(pa.valves) ? pa.valves : [];
    const valves = [];
    keys.forEach((key) => {
      if (typeof key !== "string") return;
      const parts = key.split("-");
      if (parts.length < 2) return;
      const z = parts[0];
      const ch = parts[1];
      const chInfo = channels[key] || {};
      const cell = dbVals.z && dbVals.z[z] && dbVals.z[z][ch];
      const mode = Number(chInfo[irrModeId]);
      const windowed = Number.isFinite(mode) && mode !== constant.IRR_SCHEDULED;
      const events = [];
      if (cell) {
        if (!windowed) {
          for (let i = 0; i < 6; i++) {
            const dayMins = Number(cell[startId0 + i]);
            const durMins = Number(cell[durId0 + i]) / 60;
            if (dayMins < 1440 && durMins > 0) {
              events.push({ dayMins, durMins, recipe: carried(dayMins, durMins) });
            }
          }
        } else {
          const dayMins = Number(cell[irrStartId]);
          const end = Number(cell[irrEndId]);
          const durMins = end - dayMins;
          if (dayMins < 1440 && durMins > 0) {
            events.push({ dayMins, durMins, recipe: carried(dayMins, durMins) });
          }
        }
      }
      const zoneIdx = Number(z);
      valves.push({
        id: key,
        zone: zoneNameOf(zones, zoneIdx) || z,
        name: channelLabel(channels, key) || key,
        windowed,
        tag: IRR_TAGS[mode] || "",
        events,
      });
    });
    const fresh = cloneValves(valves);
    this.savedValves = fresh;
    this.savedSig = valveSigs(fresh);
    const holding = this.state.editedCount > 0;
    this.setState((s) => ({
      loaded: true,
      recipes,
      segments: pa.recipeSegments || [],
      valves: holding ? s.valves : fresh,
      editedCount: holding ? editedCount(this.savedSig, s.valves) : 0,
    }));
  };

  armSave = (on) => {
    globs.events.publish("savePageEnable", !!on);
    if (this.props.onChange) {
      this.props.onChange({ type: "change", value: on ? this.savePage : null });
    }
  };

  commitValves = (valves) => {
    const n = editedCount(this.savedSig, valves);
    this.armSave(n > 0);
    this.setState({ valves, editedCount: n });
  };

  /** Stub. recipeList and recipeSched are what the controller will be sent
   *  once the firmware can take them. */
  saveRecipesAndSchedule = (segs) => {
    const { recipes } = this.state;
    const recipeLU = {};
    (recipes || []).forEach((r) => { recipeLU[r.recipeId] = r; });
    let curRecipe;
    const recipeSched = [];
    const recipeList = [];
    const recipePos = {};
    (segs || []).forEach((s) => {
      if (s.type === "recipe" && s.recipeId !== curRecipe) {
        if (recipePos[s.recipeId] == null) {
          recipePos[s.recipeId] = recipeList.length;
          recipeList.push(recipeLU[s.recipeId]);
        }
        recipeSched.push({ start: s.start, recipeIdx: recipePos[s.recipeId] });
        curRecipe = s.recipeId;
      }
    });
    showRecipeListSched(recipeList, recipeSched);
    return { recipeList, recipeSched };
  };

  doSave = async (field, value) => {
    const pa = this.props.parms || {};
    cl(`save ${field}`);
    await wsTrans("usa", {
      cmd: "cRest",
      uri: "/s/pearlDoser",
      method: "update",
      sessionId: globs.userData.session.sessionId,
      body: {
        zoneId: pa.zone,
        type: field,
        value,
      },
    });
  };

  /** Same channel write as DoserSchedule2.saveAdvSchedule. Scheduled valves
   *  get up to six starts; the unused slots are set to 1440. A window is one
   *  start and end. The recipe bar is stored from those runs. */
  saveAdvSchedule = async () => {
    paramIds();
    const makeDataPack = (z, c, i, d) => {
      const pack = { z, c, i, f: 2, t: Math.floor(getTime()), d };
      if (dbVals.z) {
        if (!dbVals.z[z]) dbVals.z[z] = {};
        if (!dbVals.z[z][c]) dbVals.z[z][c] = {};
        dbVals.z[z][c][i] = d;
      }
      return pack;
    };
    const {onChange,parms}=this.props
//     const pa = parms || {};
    const st=this.state
    const channels = parms.channels || {};
    const valves = this.state.valves || [];
    const byId = {};
    valves.forEach((v) => { byId[v.id] = v; });
    const entries = [];
    valves.forEach((v) => {
      (v.events || []).forEach((ev) => {
        const durMins = Number(ev.durMins) || 0;
        if (durMins <= 0) return;
        entries.push({
          valve: v.id,
          dayMins: Math.round(Number(ev.dayMins) || 0),
          durMins,
          recipe: ev.recipe || "",
        });
      });
    });
    const recipeSegments = buildCurrentRecipeSegments(entries, this.state.recipes, 0, 1440);
    await this.doSave("recipeSegments", recipeSegments);
    await this.doSave("mode", st.mode || "advGraph");
    onChange({type:"update",value:{mode:"advGraph"},})

    const byZone = {};
    const ensure = (id) => {
      const parts = String(id || "").split("-");
      if (parts.length < 2) return null;
      const z = parts[0];
      const ch = parts[1];
      if (!byZone[z]) byZone[z] = {};
      if (!byZone[z][ch]) byZone[z][ch] = { slot: 0, arr: [] };
      return byZone[z][ch];
    };
    valves.forEach((v) => ensure(v.id));
    entries.slice().sort((a, b) => a.dayMins - b.dayMins).forEach((e) => {
      const chArr = ensure(e.valve);
      if (!chArr) return;
      const [z, ch] = e.valve.split("-");
      const chan = channels[e.valve];
      const valve = byId[e.valve];
      const scheduled = chan
        ? chan[irrModeId] == constant.IRR_SCHEDULED
        : !(valve && valve.windowed);
      if (scheduled) {
        if (chArr.slot < 6) {
          chArr.arr.push(makeDataPack(+z, +ch, startId0 + chArr.slot, e.dayMins));
          chArr.arr.push(makeDataPack(+z, +ch, durId0 + chArr.slot, Math.round(e.durMins * 60)));
          chArr.slot += 1;
        }
      } else {
        chArr.arr.push(makeDataPack(+z, +ch, irrStartId, e.dayMins));
        chArr.arr.push(makeDataPack(+z, +ch, irrEndId, e.dayMins + e.durMins));
      }
    });
    valves.forEach((v) => {
      if ((v.events || []).some((ev) => (Number(ev.durMins) || 0) > 0)) return;
      if (!v.windowed) return;
      const parts = String(v.id).split("-");
      if (parts.length < 2) return;
      const chArr = ensure(v.id);
      chArr.arr.push(makeDataPack(+parts[0], +parts[1], irrStartId, 1440));
      chArr.arr.push(makeDataPack(+parts[0], +parts[1], irrEndId, 1440));
    });

    const zoneIds = Object.keys(byZone);
    let zonesSaved = 0;
    this.setState({ zonesToSave: zoneIds.length, zonesSaved: 0 });
    try {
      for (const z of zoneIds) {
        const arr = [];
        Object.keys(byZone[z]).forEach((ch) => {
          const chArr = byZone[z][ch];
          arr.push(...chArr.arr);
          for (let i = chArr.slot; i < 6; i++) {
            arr.push(makeDataPack(+z, +ch, startId0 + i, 1440));
          }
        });
        await sendArray(arr, 0, 1900, 0, false);
        zonesSaved += 1;
        this.setState({ zonesSaved });
      }
    } finally {
      this.setState({ zonesToSave: 0 });
    }
//     cl(recipeSegments)
    return recipeSegments;
  };

  renderSavingProgress() {
    const { zonesToSave, zonesSaved } = this.state;
    if (!zonesToSave) return null;
    const pct = Math.min(100, Math.round((100 * (zonesSaved || 0)) / zonesToSave));
    return (
      <div style={savingOverlay} role="dialog" aria-label="Saving">
        <div style={savingBox}>
          <div style={savingTitle}>Saving</div>
          <div style={savingTrack}>
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: "#1a73e8",
                transition: "width 0.2s ease",
              }}
            />
          </div>
          <div style={savingLabel}>
            {zonesSaved || 0} / {zonesToSave}
          </div>
        </div>
      </div>
    );
  }

  savePage = async (type) => {
    if (type === "cancel") {
      this.restoreSaved();
      return;
    }
    globs.events.publish("savePageEnable", false);
    try {
      const segments = await this.saveAdvSchedule();
      const { recipeList, recipeSched } = this.saveRecipesAndSchedule(segments);
      this.recipeList = recipeList;
      this.recipeSched = recipeSched;
      const valves = this.state.valves;
      this.savedValves = cloneValves(valves);
      this.savedSig = valveSigs(this.savedValves);
      this.setState({ editedCount: 0, segments });
      if (this.props.onChange) this.props.onChange({ type: "change", value: null });
      globs.events.publish("saveOK", true);
    } catch (err) {
      cl(err);
      this.armSave(true);
    }
  };

  restoreSaved = () => {
    const valves = cloneValves(this.savedValves);
    this.armSave(false);
    this.setState({ valves, editedCount: 0, editingId: null });
  };

  onOpenValve = (valve) => {
    if (!valve) return;
    this.setState({ editingId: valve.id, stub: "" });
  };

  onApplyValve = (valveId, events) => {
    const valves = this.state.valves.map((v) => (v.id === valveId ? { ...v, events } : v));
    this.commitValves(valves);
    this.setState({ editingId: null });
  };

  onCloseValve = () => {
    this.setState({ editingId: null });
  };

  onEditRun = (info) => {
    if (!info || !info.valve || info.index == null) return;
    const { valve, index, dayMins, durMins } = info;
    const valves = this.state.valves.map((v) => {
      if (v.id !== valve.id) return v;
      const events = (v.events || []).map((ev, i) =>
        i === index ? { ...ev, dayMins, durMins } : ev
      );
      return { ...v, events };
    });
    this.commitValves(valves);
  };

  advGraphOnChange=(v)=>{
    let {onChange}=this.props
    let st=this.state
//     cl(st.selPage,v)
    switch(v.type){
      case "change":
        cl(v)
        onChange({ type: "change", value: this.savePage });
//         onChange(v)// pass to parent
        break
//       case "click":
//         return this.onPageClick(v)
      case "update":
        this.setState(v.value)
        cl(v)
        break
    }
  }

  render() {
    const { loaded, recipes, segments, valves, stub, nowMins, editingId,
      editedCount, mode } = this.state;
    const {parms,onChange}=this.props
//     const {pa.mode}=this.props.parms
//     cl(parms.mode)
    if (!loaded) {
      return (
        <div style={{ padding: 24 }}>
          <LoadingSpinner />
        </div>
      );
    }
    const editing = editingId ? valves.find((v) => v.id === editingId) : null;
    return (
      <div style={{position:"relative"}}>
        <AdvScheduleGraph3
          recipes={recipes}
          segments={segments}
          valves={valves}
          stub={stub}
          mode={mode}
          onChange={this.advGraphOnChange}
          nowMins={nowMins}
          editedCount={editedCount || 0}
          onOpenValve={this.onOpenValve}
          onEditRun={this.onEditRun}
        />
        {this.renderSavingProgress()}
        {editing ? (
          <ValveEditor
            valve={editing}
            recipes={recipes}
            onApply={this.onApplyValve}
            onClose={this.onCloseValve}
          />
        ) : null}
        {mode!="advGraph"&&
          <div style={{position:"absolute",top:0,width:"100%",
            height:"100%",background:"black",opacity:0.1}}/>
        }
      </div>
    );
  }
}
